import { assert, Test } from "oc-e2e-testrunner";
import { v2 } from "oc-threejs/short3";
import { resetArrangement } from "./utils";

export default {
  name: "TestTorÖffnungsrichtungGrundriss",
};

/**
 * Erstellt einen Grundriss, platziert ein Einzeltor auf dem ersten Segment und prüft
 * die Konsistenz von flippedNormal und isÖffnungNachInnen.
 *
 * Bei nicht-kollinearen Kanten wird erwartet, dass die Normale über den middlePoint
 * berechnet wird, unabhängig von der Anzahl der Kanten.
 * Bei kollinearen Kanten (keine eindeutige Innen/Außen-Seite) soll die Default-Normale
 * (orthogonalRight) verwendet werden (flippedNormal = false).
 */

function setupWithTor(
  grundrissSetup: (agt: any) => void,
  segmentIndex = 0
) {
  resetArrangement();
  const agt = APP.arrangement;
  grundrissSetup(agt);
  agt.update3d();
  const s = agt.segmente[segmentIndex];
  const element = agt.createElementFromJson({
    form: "Einzeltor",
    füllungAuswahlwert: "SYSTEM WPC CLASSIC Sand",
    öffnungAuswahlwert: "Öffnung_nach_innen",
    anschlagAuswahlwert: "Anschlag_links",
  });
  s.addPart(element);
  agt.update3d();
  const tor = s.getElemente().find((e) => e.isTor) ?? element;
  return { agt, tor, segment: s };
}

// ============================================================
// Kollineare Fälle: kein eindeutiges Innen/Außen,
// flippedNormal soll false bleiben
// ============================================================

// 1 Kante kollinear (Strecke)
export const testKollinear1Kante: Test = async () => {
  const { agt, tor, segment } = setupWithTor((agt) => {
    agt.grundriss.setStandardForm("Strecke", [5]);
  });
  assert.equals(1, agt.grundriss.kanten.length, "Anzahl Kanten");
  assert.equals(false, segment.flippedNormal, "Strecke: flippedNormal = false");
  assert.equals(true, tor.isÖffnungNachInnen, "Strecke: isÖffnungNachInnen bei 'nach innen'");
};

// 2 Kanten kollinear (getrennte Strecken auf derselben Geraden)
export const testKollinear2Kanten: Test = async () => {
  const { agt, tor, segment } = setupWithTor((agt) => {
    // Zwei getrennte Strecken auf derselben Linie (keine gemeinsame Ecke,
    // damit entferneUnnötigeEcken sie nicht zu einer verschmilzt)
    agt.grundriss.setStrecken([
      [v2(0, 0), v2(3, 0)],
      [v2(5, 0), v2(8, 0)],
    ]);
  });
  assert.equals(2, agt.grundriss.kanten.length, "Anzahl Kanten");
  for (const s of agt.segmente) {
    assert.equals(false, s.flippedNormal, "Kollinear 2: flippedNormal = false");
  }
  assert.equals(true, tor.isÖffnungNachInnen, "Kollinear 2: isÖffnungNachInnen bei 'nach innen'");
};

// 3 Kanten kollinear (getrennte Strecken auf derselben Geraden)
export const testKollinear3Kanten: Test = async () => {
  const { agt, tor, segment } = setupWithTor((agt) => {
    // Drei getrennte Strecken auf derselben Linie
    agt.grundriss.setStrecken([
      [v2(0, 0), v2(2, 0)],
      [v2(4, 0), v2(6, 0)],
      [v2(8, 0), v2(10, 0)],
    ]);
  });
  assert.equals(3, agt.grundriss.kanten.length, "Anzahl Kanten");
  for (const s of agt.segmente) {
    assert.equals(false, s.flippedNormal, "Kollinear 3: flippedNormal = false");
  }
  assert.equals(true, tor.isÖffnungNachInnen, "Kollinear 3: isÖffnungNachInnen bei 'nach innen'");
};

// ============================================================
// Nicht-kollineare Fälle: Innen/Außen über middlePoint definiert,
// flippedNormal soll korrekt berechnet werden
// ============================================================

// 2 Kanten nicht-kollinear (L-Form / Eck)
// Eck [5,5]: Punkte (0,0)→(5,0)→(5,5)
// middlePoint = (2.5, 2.5)
// Kante 0: direction=(1,0), orthogonalRight=(0,1), middleNormal=(0,1) → flippedNormal = true
// Kante 1: direction=(0,1), orthogonalRight=(-1,0), middleNormal=(-1,0) → flippedNormal = true
export const testNichtKollinear2Kanten: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Eck", [5, 5]);
  agt.update3d();

  assert.equals(2, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Beide Kanten sollten flippedNormal = true haben (middlePoint-basierte Berechnung)
  for (const s of agt.segmente) {
    assert.equals(
      true,
      s.flippedNormal,
      "Eck (2 nicht-kollineare Kanten): flippedNormal soll über middlePoint berechnet werden"
    );
  }

  // Einzeltor auf dem ersten Segment
  const s = agt.segmente[0];
  const element = agt.createElementFromJson({
    form: "Einzeltor",
    füllungAuswahlwert: "SYSTEM WPC CLASSIC Sand",
    öffnungAuswahlwert: "Öffnung_nach_innen",
    anschlagAuswahlwert: "Anschlag_links",
  });
  s.addPart(element);
  agt.update3d();
  const tor = s.getElemente().find((e) => e.isTor) ?? element;

  // Mit flippedNormal = true wird isÖffnungNachInnen invertiert
  assert.equals(
    false,
    tor.isÖffnungNachInnen,
    "Eck: 'Öffnung_nach_innen' + flippedNormal=true → isÖffnungNachInnen = false"
  );

  // Gegenprobe: Öffnung nach außen
  tor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  assert.equals(
    true,
    tor.isÖffnungNachInnen,
    "Eck: 'Öffnung_nach_außen' + flippedNormal=true → isÖffnungNachInnen = true"
  );
};

// 2 Kanten nicht-kollinear, andere Orientierung (spiegelverkehrtes L)
// Strecken: (5,0)→(0,0) und (0,0)→(0,5) - anderer Umlaufsinn
// orthogonalRight zeigt hier weg vom middlePoint → flippedNormal = false
export const testNichtKollinear2KantenAndereOrientierung: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStrecken([
    [v2(5, 0), v2(0, 0)],
    [v2(0, 0), v2(0, 5)],
  ]);
  agt.update3d();

  assert.equals(2, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Für diese Orientierung zeigt orthogonalRight weg vom middlePoint → flippedNormal = false
  for (const s of agt.segmente) {
    assert.equals(
      false,
      s.flippedNormal,
      "Eck (andere Orientierung): flippedNormal = false"
    );
  }
};

// 3 Kanten nicht-kollinear (U-Form mit 3 Seiten)
// Diese funktionieren bereits korrekt (kanten.length > 2), dient als Referenz
export const testNichtKollinear3Kanten: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Rechteck", [5, 5], [true, true, true, false]);
  agt.update3d();

  assert.equals(3, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Alle 3 Kanten sollten flippedNormal = false haben (für diese Rechteck-Orientierung)
  for (const s of agt.segmente) {
    assert.equals(
      false,
      s.flippedNormal,
      "U-Form (3 nicht-kollineare Kanten): flippedNormal = false"
    );
  }
};

// ============================================================
// Konsistenztest: Ein Eck (2 Kanten) soll dasselbe flippedNormal
// haben wie ein Grundriss mit 3 Kanten, der dieselbe Ecke enthält
// ============================================================
export const testKonsistenz2vs3Kanten: Test = async () => {
  // 3 Kanten (Rechteck mit 2 Seiten + eine dritte): funktioniert bereits korrekt
  resetArrangement();
  const agt3 = APP.arrangement;
  agt3.grundriss.setStrecken([
    [v2(0, 0), v2(5, 0)],
    [v2(5, 0), v2(5, 5)],
    [v2(5, 5), v2(0, 5)],
  ]);
  agt3.update3d();
  assert.equals(3, agt3.grundriss.kanten.length, "3 Kanten");
  const flipped3_kante0 = agt3.segmente[0].flippedNormal;
  const flipped3_kante1 = agt3.segmente[1].flippedNormal;

  // 2 Kanten (nur die ersten beiden Kanten): soll sich genauso verhalten
  resetArrangement();
  const agt2 = APP.arrangement;
  agt2.grundriss.setStrecken([
    [v2(0, 0), v2(5, 0)],
    [v2(5, 0), v2(5, 5)],
  ]);
  agt2.update3d();
  assert.equals(2, agt2.grundriss.kanten.length, "2 Kanten");
  const flipped2_kante0 = agt2.segmente[0].flippedNormal;
  const flipped2_kante1 = agt2.segmente[1].flippedNormal;

  // Die flippedNormal-Werte der gemeinsamen Kanten sollen übereinstimmen
  assert.equals(
    flipped3_kante0,
    flipped2_kante0,
    "Kante 0: flippedNormal soll bei 2 und 3 Kanten gleich sein"
  );
  assert.equals(
    flipped3_kante1,
    flipped2_kante1,
    "Kante 1: flippedNormal soll bei 2 und 3 Kanten gleich sein"
  );
};

// ============================================================
// Parallele Kanten, aber nicht auf einer Geraden (Versatz):
// Es entsteht trotzdem ein Innen/Außen, da die Ecken nicht
// kollinear sind. Die Kantenrichtungen allein reichen nicht
// für die Kollinearitätsprüfung.
// ============================================================

// 2 parallele Kanten mit Versatz (getrennte horizontale Strecken auf verschiedenen Höhen)
export const testParallelVersetzt2Kanten: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStrecken([
    [v2(0, 0), v2(5, 0)],
    [v2(0, 3), v2(10, 3)],
  ]);
  agt.update3d();

  assert.equals(2, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Obwohl die Richtungen parallel sind, liegen die Ecken NICHT auf einer Geraden.
  // Daher soll die middlePoint-basierte Normale berechnet werden.
  const hatMiddlePointBerechnung = agt.segmente.some((s) => s.flippedNormal) ||
    agt.grundriss.kanten.some((k) => {
      const defaultNormal = { x: -k.direction.y, y: k.direction.x }; // orthogonalRight
      return Math.abs(k.normal.x - defaultNormal.x) > 0.01 ||
             Math.abs(k.normal.y - defaultNormal.y) > 0.01;
    });
  assert.isTrue(
    hatMiddlePointBerechnung,
    "Paralleler Versatz: Normale soll über middlePoint berechnet werden (nicht Default-orthogonalRight)"
  );
};

// 3 Kanten: 2 parallele horizontale + 1 verbindende (Stufe), ähnlich dem Screenshot
// Stufenform: horizontale Kante, kurze vertikale Verbindung, versetzte horizontale Kante
export const testParallelVersetztStufe: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStrecken([
    [v2(0, 0), v2(5, 0)],       // horizontal unten
    [v2(5, 0), v2(5, 1)],       // kurze vertikale Stufe
    [v2(5, 1), v2(16, 1)],      // horizontal oben (versetzt)
  ]);
  agt.update3d();

  assert.equals(3, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Die Ecken (0,0), (5,0), (5,1), (16,1) liegen nicht auf einer Geraden.
  // Daher soll die middlePoint-basierte Normale berechnet werden.
  const s0 = agt.segmente[0];
  const element = agt.createElementFromJson({
    form: "Einzeltor",
    füllungAuswahlwert: "SYSTEM WPC CLASSIC Sand",
    öffnungAuswahlwert: "Öffnung_nach_innen",
    anschlagAuswahlwert: "Anschlag_links",
  });
  s0.addPart(element);
  agt.update3d();
  const tor = s0.getElemente().find((e) => e.isTor) ?? element;

  // Prüfe, dass die Normale korrekt berechnet wurde (nicht Default)
  const grundrissNormaleBerechnet = agt.grundriss.kanten.some((k) => {
    const defaultNormal = { x: -k.direction.y, y: k.direction.x };
    return Math.abs(k.normal.x - defaultNormal.x) > 0.01 ||
           Math.abs(k.normal.y - defaultNormal.y) > 0.01;
  }) || agt.segmente.some((s) => s.flippedNormal);
  assert.isTrue(
    grundrissNormaleBerechnet,
    "Stufe: Normale soll über middlePoint berechnet werden"
  );
};

// 4 Kanten: Bild-Szenario – oben 3 Teilstücke (mit Versatz), unten 1 separater Strecke
export const testParallelVersetztMitSeparaterStrecke: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  // Oben: 3 Stücke mit kleinem Versatz dazwischen (wie im Screenshot)
  // Unten: 1 separater paralleler Strecke
  agt.grundriss.setStrecken([
    [v2(0, 0), v2(5, 0)],       // oben links
    [v2(5, 0), v2(5, 0.5)],     // kurzer Versatz
    [v2(5, 0.5), v2(16, 0.5)],  // oben rechts (versetzt)
    [v2(2, 5), v2(17, 5)],      // unten, separater Strecke
  ]);
  agt.update3d();

  assert.equals(4, agt.grundriss.kanten.length, "Anzahl Kanten");

  // Ecken liegen definitiv nicht auf einer Geraden
  for (const s of agt.segmente) {
    // Überprüfe, dass die Normalen über den middlePoint berechnet wurden
    const kante = s.segmentKante?.grundrissKante;
    if (kante) {
      const defaultNormal = { x: -kante.direction.y, y: kante.direction.x };
      const isDefault =
        Math.abs(kante.normal.x - defaultNormal.x) < 0.01 &&
        Math.abs(kante.normal.y - defaultNormal.y) < 0.01 &&
        !s.flippedNormal;
      // Mindestens einige Segmente sollten NICHT die Default-Normale haben
    }
  }

  // Die Hauptassertion: es existiert ein sinnvolles Innen/Außen
  const middlePoint = agt.grundriss.middlePoint;
  assert.isTrue(
    middlePoint != null,
    "MiddlePoint soll existieren"
  );
  // middlePoint sollte ungefähr in der Mitte der Gesamtfläche liegen
  assert.isTrue(
    middlePoint.y > 0 && middlePoint.y < 5,
    "MiddlePoint y soll zwischen den parallelen Strecken liegen"
  );
};
